
import { parseDSL } from '../lib/parser';
import { dataStorageService } from './dataStorageService';
import { security } from '../lib/auth/security';
import { appConstants } from '../constants/appConstants';

/**
 * DataSyncService manages synchronization of external DSL private files.
 */
export const dataSyncService = {
    /**
     * Checks if there's a new version available on the server.
     * Supports Hash_A for personal configuration override.
     */
    async checkUpdate(hashA = null) {
        try {
            const response = await fetch(appConstants.apiManifestUrl, { 
                mode: 'cors',
                cache: 'no-cache' 
            });
            if (!response.ok) throw new Error(`Failed to fetch manifest: ${response.statusText}`);
            
            let serverManifest = await response.json();
            
            // Personal Config Override
            if (hashA) {
                // 1. Перевіряємо чи є користувач у глобальному реєстрі (user_configs)
                const userConfigPath = serverManifest.user_configs ? serverManifest.user_configs[hashA] : null;

                if (!userConfigPath) {
                    console.warn(`User ${hashA} not found in user_configs registry.`);
                    return { isRequired: false, authError: 'DELETED_USER' };
                }

                // 2. Якщо є, завантажуємо персональний конфіг за вказаним шляхом
                const configUrl = appConstants.apiBaseUrl + userConfigPath;
                try {
                    const configResponse = await fetch(configUrl, { mode: 'cors', cache: 'no-cache' });
                    if (configResponse.ok) {
                        const personalConfig = await configResponse.json();
                        // Заміщуємо публічні файли персональними
                        serverManifest = {
                            ...serverManifest,
                            files: personalConfig.files,
                            isPersonal: true,
                            lastUpdate: personalConfig.lastUpdate
                        };
                    } else {
                        console.error(`Failed to fetch personal config at ${configUrl}`);
                        return { isRequired: false, authError: 'DELETED_USER' };
                    }
                } catch (configError) {
                    console.error(`Error loading personal config:`, configError);
                    return { isRequired: false }; // Network error, don't logout
                }
            }

            const localManifest = await dataStorageService.getVersionInfo();

            if (!localManifest || (hashA && !localManifest.isPersonal) || (!hashA && localManifest.isPersonal)) {
                return { 
                    isRequired: true, 
                    manifest: serverManifest, 
                    changedFiles: Object.keys(serverManifest.files) 
                };
            }

            const changedFiles = [];
            for (const [fileKey, fileInfo] of Object.entries(serverManifest.files)) {
                if (!localManifest.files[fileKey] || localManifest.files[fileKey].hash !== fileInfo.hash) {
                    changedFiles.push(fileKey);
                }
            }

            return {
                isRequired: changedFiles.length > 0,
                manifest: serverManifest,
                changedFiles
            };
        } catch (error) {
            console.error('Update check failed:', error);
            return { isRequired: false, error };
        }
    },

    /**
     * Downloads and parses a specific file.
     * Decrypts if hashB is provided and file is .enc
     */
    async updateFile(fileKey, fileInfo, hashB = null) {
        try {
            const url = appConstants.apiBaseUrl + fileInfo.path;
            const response = await fetch(url, { 
                mode: 'cors',
                cache: 'no-cache' 
            });
            if (!response.ok) throw new Error(`Failed to fetch ${fileKey}: ${response.statusText}`);

            let content = await response.text();
            
            // Decryption if needed
            if (fileInfo.path.endsWith('.enc')) {
                if (!hashB) {
                    throw new Error(`Encrypted file ${fileKey} requires a decryption key.`);
                }
                content = security.decryptContent(content, hashB);
            }

            const parsedData = parseDSL(content);
            const normalizedData = normalizeParsedData(parsedData);
            
            await dataStorageService.saveData(fileKey, normalizedData);
            return true;
        } catch (error) {
            console.error(`Error updating ${fileKey}:`, error);
            throw error;
        }
    },

    /**
     * Updates all changed files and saves the new manifest.
     */
    async performUpdate(manifest, changedFiles, hashB = null) {
        for (const fileKey of changedFiles) {
            const fileInfo = manifest.files[fileKey];
            await this.updateFile(fileKey, fileInfo, hashB);
        }
        await dataStorageService.saveVersionInfo(manifest);
    }
};

/**
 * Normalizes parsed data by cleaning up common DSL artifacts and placeholders.
 */
function normalizeParsedData(data) {
    if (!Array.isArray(data)) return data;

    return data.map(node => {
        if (!node.scenarioItems || !Array.isArray(node.scenarioItems)) return node;

        const cleanedItems = node.scenarioItems.filter(item => {
            // Check for empty or placeholder translations like {"en-US": "--"}
            if (item.translations) {
                const keys = Object.keys(item.translations);
                
                // If there's only one key and its value is '--', it's likely a DSL artifact
                if (keys.length === 1 && item.translations[keys[0]] === '--') {
                    return false;
                }
                
                // If translations object is empty
                if (keys.length === 0) {
                    return false;
                }
            }
            return true;
        });

        return {
            ...node,
            scenarioItems: cleanedItems
        };
    });
}
